import { Elements, PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import * as stripeJs from "@stripe/stripe-js";
import { PaymentRequest } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { Result } from "safe-portals";
import * as routes from "../../common/routes";

import { DonationMetadata, JourneyContext, Member } from "@flow/common/types";
import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";
import { Typography } from "@mui/material";
interface StripePaymentRequestButtonProps {
  amount: number;
  metadata: DonationMetadata;
  hide_donation: boolean;
  stripePromise: PromiseLike<stripeJs.Stripe | null> | null;
  ctx: JourneyContext;
  action_guid: string;
  variant_idx: number;
  isRegular: boolean;
  useMemberPrefill: boolean;
  onPaymentSuccess: (donation_attempt_guid: string, member: Member) => void;
}

// eslint-disable-next-line woke/all
/**
 * `StripePaymentRequestButton` and `StripePaymentRequestButtonInternal`
 * Are wrapper components for the Stripe `PaymentRequestButtonElement`
 * The `PaymentRequestButtonElement` is from the Stripe js library and is used to render
 * a mobile wallets payment button. This component automatically renders as a Google or Apple Pay
 * button dependant on browser and device environment and conditional on there being a
 * valid payment method attached to the wallet
 */
export function StripePaymentRequestButton(props: StripePaymentRequestButtonProps) {
  return (
    <Elements stripe={props.stripePromise}>
      <StripePaymentRequestButtonInternal
        amount={props.amount}
        metadata={props.metadata}
        hide_donation={props.hide_donation}
        ctx={props.ctx}
        action_guid={props.action_guid}
        variant_idx={props.amount}
        isRegular={props.isRegular}
        useMemberPrefill={props.useMemberPrefill}
        onPaymentSuccess={props.onPaymentSuccess}
      />
    </Elements>
  );
}

const MOBILE_WALLET_PAYMENT_LABEL = "38 Degrees";

function StripePaymentRequestButtonInternal(props: Omit<StripePaymentRequestButtonProps, "stripePromise">) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (stripe) {
      setIsLoading(true);
      const pr = stripe.paymentRequest({
        country: "GB",
        currency: "gbp",
        total: {
          label: MOBILE_WALLET_PAYMENT_LABEL,
          amount: Math.round(props.amount * 100), // amount required here in subunit, i.e. pence.
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ["link"],
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment()
        .then((result) => {
          if (result) {
            setPaymentRequest(pr);
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [stripe]);

  useEffect(() => {
    if (stripe) {
      setErrorMessage(undefined);
      paymentRequest?.update({
        total: {
          label: MOBILE_WALLET_PAYMENT_LABEL,
          amount: Math.round(props.amount * 100), // amount required here in subunit, i.e. pence.
        },
      });
      if (paymentRequest) {
        registerOnPaymentMethodEventHandler(paymentRequest);
      }
    }
  }, [props.amount, props.isRegular]);

  const completePayment = (donation_attempt_guid: string, member: Member) =>
    props.onPaymentSuccess(donation_attempt_guid, member);

  const createPaymentIntent = async (paymentMethodId: string, member: Member) => {
    setIsLoading(true);
    // Call backend to create payment intent
    const intent_result = await routes.createStripePaymentIntent.call({
      action_guid: props.action_guid,
      variant_idx: props.variant_idx,
      payment_method_id: paymentMethodId,
      journey_context: { ...props.ctx, member },
      amount: props.amount,
      metadata: props.metadata,
      hide_donation: props.hide_donation,
      is_subscription: props.isRegular,
      use_prefill_member_data: props.useMemberPrefill,
    });
    setIsLoading(false);
    if (Result.isErr(intent_result)) {
      setErrorMessage(intent_result.error);
      return;
    }

    return intent_result.ok;
  };

  const REQUIRES_ACTION = "requires_action";
  const REQUIRES_PAYMENT_METHOD = "requires_payment_method";

  const registerOnPaymentMethodEventHandler = (paymentRequest: PaymentRequest) => {
    paymentRequest.off("paymentmethod");
    paymentRequest.on("paymentmethod", async function (event) {
      const paymentMethodId = event.paymentMethod.id;
      const { payerName: name, payerEmail: email } = event;
      const updatedMember = { ...props.ctx.member, ...{ name, email } };

      const intent = await createPaymentIntent(paymentMethodId, updatedMember);

      if (intent === undefined || stripe === null) {
        setErrorMessage("Something has gone wrong. Please refresh the page and try again.");
        event.complete("fail");
        return;
      }

      const { client_secret, donation_attempt_guid, status } = intent;

      // For subscriptions, the Stripe code in Flow backend handles payment confirmation
      // as part of the createPaymentIntent call
      if (props.isRegular) {
        if (status === REQUIRES_ACTION || status === REQUIRES_PAYMENT_METHOD) {
          // Check if the PaymentIntent requires any actions and, if so, let Stripe.js handle the flow.
          await stripe.confirmCardPayment(intent.client_secret);
        }

        completePayment(donation_attempt_guid, updatedMember);
        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.
        event.complete("success");
        return;
      }
      // Confirm the PaymentIntent without handling potential next actions (yet).
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        client_secret,
        { payment_method: event.paymentMethod.id },
        { handleActions: false }
      );

      if (confirmError) {
        setErrorMessage(confirmError.message);
        // Report to the browser that the payment failed, prompting it to
        // re-show the payment interface, or show an error message and close
        // the payment interface.
        event.complete("fail");
        return;
      }
      // Report to the browser that the confirmation was successful, prompting
      // it to close the browser payment method collection interface.
      event.complete("success");

      // Check if the PaymentIntent requires any actions and, if so, let Stripe.js handle the flow.
      if (paymentIntent?.status === REQUIRES_ACTION) {
        const { error } = await stripe.confirmCardPayment(client_secret);
        if (error) {
          setErrorMessage(error.message);
          event.complete("fail");
        } else {
          completePayment(donation_attempt_guid, updatedMember);
        }
      } else {
        completePayment(donation_attempt_guid, updatedMember);
      }
    });
  };

  const handlePaymentButtonClick = (e: stripeJs.StripePaymentRequestButtonElementClickEvent) => {
    // PaymentRequestButtonElement exposes no prop to render a disabled state
    // looks like this might be something to do with Apple's Human Interface Guidelines not allowing disabled state (https://github.com/stripe-archive/react-stripe-elements/issues/203)
    if (props.amount <= 0) {
      e.preventDefault();
      setErrorMessage("Please input an amount before selecting payment method");
    }
  };

  if (paymentRequest) {
    const stripePaymentRequestButtonOptions: stripeJs.StripePaymentRequestButtonElementOptions = {
      style: {
        paymentRequestButton: {
          type: "donate",
        },
      },
      disableMultipleButtons: true,
      paymentRequest,
    };

    return (
      <>
        <PaymentRequestButtonElement options={stripePaymentRequestButtonOptions} onClick={handlePaymentButtonClick} />
        {errorMessage && (
          <Typography variant="body2" color="error" sx={{ marginTop: "0.25rem !important" }}>
            {errorMessage}
          </Typography>
        )}
      </>
    );
  }

  return isLoading ? <MemberLoadingSpinner /> : null;
}
